@let loaded = relationshipsSelectors.finishedLoading | select:false;
@let isMdOrAbove = viewportSelectors.isMdAndUp | select;
@let linkedAccounts = relationshipsSelectors.linkedAccounts | select:[];
@let dependents = relationshipsSelectors.dependents | select:[];
@let guardians = relationshipsSelectors.guardians | select:[];
@let buttonLabelSize = isMdOrAbove ? 'default' : 'small';

<div mxHiddenUntilLoaded [reveal]="loaded" class="flex flex-col gap-10">
  <!-- Linked Accounts -->
  <div class="flex flex-col gap-3">
    <p class="text-headline-medium">Linked Accounts</p>
    <div class="flex flex-col md:flex-row gap-3">
      @for (account of linkedAccounts; track $index) {
        <ng-container *ngTemplateOutlet="relatedPatron; context: {relatedPatron: account, patronType: 'linked'}"/>
      } @empty {
        <p class="text-body-large pl-4">None.</p>
      }
    </div>
  </div>


  <!-- Dependents -->
  @if (dependents?.length) {
    <div class="flex flex-col gap-3">
      <div class="flex flex-row justify-between items-center gap-2">
        <p class="text-headline-medium">Dependents</p>
        @if (isMdOrAbove) {
          <ng-container *ngTemplateOutlet="dependentTotals"/>
        }
      </div>

      <div class="flex flex-col md:flex-row gap-3">
        @for (dependent of dependents; track $index) {
          <ng-container *ngTemplateOutlet="relatedPatron; context: {relatedPatron: dependent, patronType: 'dependent'}"/>
        }
      </div>

      @if (!isMdOrAbove) {
        <div class="self-end">
          <ng-container *ngTemplateOutlet="dependentTotals"/>
        </div>
      }
    </div>
  }

  <!-- Guardians -->
  @if (guardians?.length) {
    <div class="flex flex-col gap-3">
      <p class="text-headline-medium">Guardians</p>
      <div class="flex flex-col md:flex-row gap-3">
        @for (guardian of guardians; track $index) {
          <ng-container *ngTemplateOutlet="relatedPatron; context: {relatedPatron: guardian, patronType: 'guardian'}"/>
        }
      </div>
    </div>
  }
</div>

<ng-template #relatedPatron let-relatedPatron="relatedPatron" let-patronType="patronType">
  <mat-card class="w-full md:w-1/2 lg:w-1/3">
    <mat-card-content class="flex flex-col gap-3">
      @let blockCount = statelessSelectors.blockCountFor(relatedPatron) | select:0;

      <!-- First & Last Name (always shown) -->
      <div class="flex flex-row gap-1 items-center">
        <!-- Blocked Icon (Dependent only, if dependent patron has any blocks) -->
        @if (patronType === 'dependent' && blockCount > 0) {
          <mat-icon matTooltip="Account has blocks" class="mat-icon-size-6 shrink-0 text-error">error_outline</mat-icon>
        }
        <p class="text-title-large">{{ statelessSelectors.fullNameFor(relatedPatron) | select }}</p>
      </div>

      <div [mxDetailFieldDefaults]="{ titleCSSClasses: 'text-title-medium', valueCSSClasses: 'text-body-large', truncateTitleText: false,
                                      asRow: true, fullWidth: true }">
        <!-- Ready for Pickup (if linked or dependent) -->
        @if (patronType === 'linked' || patronType === 'dependent') {
          <mx-detail-field titleText="Ready for Pickup:" [value]="statelessSelectors.readyForPickupCountFor(relatedPatron) | select"/>
        }

        <!-- Dependent-specific fields -->
        @if (patronType === 'dependent') {
          @let overdueAmount = statelessSelectors.overdueCountFor(relatedPatron) | select:0;
          @let hasOverdue = overdueAmount > 0;
          @let balanceLimit = statelessSelectors.patronTypeBalanceLimitFor(relatedPatron) | select:0;
          @let balance = statelessSelectors.accountBalanceFor(relatedPatron) | select:0;
          @let isBalanceOverLimit = balance >= balanceLimit;

          <mx-detail-field titleText="Overdue:" [value]="overdueAmount"
                           [valueCSSClasses]="`text-body-large ${hasOverdue ? 'text-error' : ''}`"
                           [titleCSSClasses]="`text-title-medium ${hasOverdue ? 'text-error' : ''}`"/>
          <mx-detail-field titleText="Items Out:" [value]="statelessSelectors.itemsOutCountFor(relatedPatron) | select"/>
          <mx-detail-field titleText="Total Holds:" [value]="statelessSelectors.activeHoldCountFor(relatedPatron) | select"/>
          <mx-detail-field titleText="Balance:" [value]="balance | format:'CURRENCY'"
                           [valueCSSClasses]="`text-body-large ${isBalanceOverLimit ? 'text-error' : ''}`"
                           [titleCSSClasses]="`text-title-medium ${isBalanceOverLimit ? 'text-error' : ''}`"/>
        }
      </div>

      <!-- TODO:  This is hidden temporarily, until we have a concrete way of determining whether the current org/patron type combination allows the display
      of dependent details. -->
      <!--  @if (patronType === 'dependent') {-->
      <!--    <px-arrow-text-button label="View Account" [labelSize]="buttonLabelSize" routeName="DEPENDENT_DETAILS" -->
      <!--                          [routeParams]="{id: relatedPatron.patronId}" class="self-end"/>-->
      <!--  }-->
    </mat-card-content>
  </mat-card>
</ng-template>

<ng-template #dependentTotals>
  @if (dependents.length > 1) {
    <div class="flex flex-col md:w-1/2 md:max-w-[15rem] md:pt-1 pr-4 md:pr-0">
      <div [mxDetailFieldDefaults]="{ titleCSSClasses: 'text-title-medium', valueCSSClasses: 'text-body-large', truncateTitleText: false,
                                          asRow: true, fullWidth: true, truncateValue: false }">
        <mx-detail-field titleText="Total Ready for Pickup:" [value]="relationshipsSelectors.dependentsReadyForPickupTotal | select"/>
        <mx-detail-field titleText="Total Overdue:" [value]="relationshipsSelectors.dependentsOverdueTotal | select"/>
        <mx-detail-field titleText="Total Balance:" [value]="relationshipsSelectors.dependentsBalanceTotal | select | format:'CURRENCY'"/>
      </div>
    </div>
  }
</ng-template>
